import type { OrderHistoryFilters } from '@/api/orders/orderTypes';
import {
  DateRangePickerButton,
  FilterOptionButton,
} from '@/components/common/FilterButtons';
import { Input } from '@/components/common/Input';
import type { SubscriptionState } from '@/domain/Order';
import { useEffect, useState } from 'react';

const STATUS_FILTERS: Array<{
  label: string;
  value: SubscriptionState | 'All';
}> = [
  { label: 'All', value: 'All' },
  { label: 'Active', value: 'Active' },
  { label: 'Inactive', value: 'Inactive' },
  { label: 'Churned', value: 'Churned' },
];

type TimeFilter =
  | 'This week'
  | 'Last week'
  | 'This month'
  | 'Last month'
  | 'Custom';

const TIME_FILTERS: Array<{ label: string; value: TimeFilter }> = [
  { label: 'This week', value: 'This week' },
  { label: 'Last week', value: 'Last week' },
  { label: 'This month', value: 'This month' },
  { label: 'Last month', value: 'Last month' },
];

interface OrderHistoryFiltersProps {
  filters: OrderHistoryFilters;
  onFiltersChange: (filters: OrderHistoryFilters) => void;
}

// Format date as YYYY-MM-DD using local time (avoids UTC conversion issues)
const formatDateLocal = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getDateRange = (
  filter: TimeFilter,
): { date_from?: string; date_to?: string } => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (filter) {
    case 'This week': {
      const dayOfWeek = today.getDay();
      const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() + diffToMonday);
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      return {
        date_from: formatDateLocal(startOfWeek),
        date_to: formatDateLocal(endOfWeek),
      };
    }
    case 'Last week': {
      const dayOfWeek = today.getDay();
      const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      const thisMonday = new Date(today);
      thisMonday.setDate(today.getDate() + diffToMonday);
      const startOfLastWeek = new Date(thisMonday);
      startOfLastWeek.setDate(thisMonday.getDate() - 7);
      const endOfLastWeek = new Date(startOfLastWeek);
      endOfLastWeek.setDate(startOfLastWeek.getDate() + 6);
      return {
        date_from: formatDateLocal(startOfLastWeek),
        date_to: formatDateLocal(endOfLastWeek),
      };
    }
    case 'This month': {
      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      return {
        date_from: formatDateLocal(startOfMonth),
        date_to: formatDateLocal(endOfMonth),
      };
    }
    case 'Last month': {
      const startOfLastMonth = new Date(
        today.getFullYear(),
        today.getMonth() - 1,
        1,
      );
      const endOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
      return {
        date_from: formatDateLocal(startOfLastMonth),
        date_to: formatDateLocal(endOfLastMonth),
      };
    }
    default:
      return {};
  }
};

export function OrderHistoryFilters({
  filters,
  onFiltersChange,
}: OrderHistoryFiltersProps) {
  const [activeStatus, setActiveStatus] = useState<string>('All');
  const [activeTime, setActiveTime] = useState<TimeFilter>('This week');
  const [searchValue, setSearchValue] = useState('');
  const [customDateRange, setCustomDateRange] = useState({
    from: '2000-01-01',
    to: '2099-12-31',
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      onFiltersChange({ ...filters, search: searchValue || undefined });
    }, 500);

    return () => clearTimeout(timer);
  }, [searchValue]);

  const handleStatusChange = (statusLabel: string) => {
    setActiveStatus(statusLabel);
    const filter = STATUS_FILTERS.find((f) => f.label === statusLabel);
    onFiltersChange({
      ...filters,
      state: filter?.value === 'All' ? undefined : filter?.value,
    });
  };

  const handleTimeChange = (time: TimeFilter) => {
    setActiveTime(time);
    if (time === 'Custom') {
      return;
    }
    const dateRange = getDateRange(time);
    onFiltersChange({
      ...filters,
      ...dateRange,
    });
  };

  const handleCustomDateRangeChange = (from: string, to: string) => {
    setCustomDateRange({ from, to });
    setActiveTime('Custom');
    onFiltersChange({
      ...filters,
      date_from: from || undefined,
      date_to: to || undefined,
    });
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      onFiltersChange({ ...filters, search: searchValue });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3">
        {STATUS_FILTERS.map((filter) => (
          <FilterOptionButton
            key={filter.value}
            label={filter.label}
            isActive={activeStatus === filter.value}
            onClick={() => handleStatusChange(filter.value)}
          />
        ))}
      </div>

      <div className="flex flex-wrap gap-3">
        {TIME_FILTERS.map((filter) => (
          <FilterOptionButton
            key={filter.value}
            label={filter.label}
            isActive={activeTime === filter.value}
            onClick={() => handleTimeChange(filter.value)}
          />
        ))}

        <DateRangePickerButton
          dateFrom={customDateRange.from}
          dateTo={customDateRange.to}
          onDateRangeChange={handleCustomDateRangeChange}
          isActive={activeTime === 'Custom'}
        />
      </div>

      <div className="flex w-56 gap-2">
        <Input
          type="text"
          placeholder="Search"
          label="Filter by Name"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          onKeyDown={handleSearchKeyPress}
        />
      </div>
    </div>
  );
}
